/**
 * Dashboard card registry + the per-user dashboard layout.
 *
 * One entry per arrangeable dashboard surface. Used by three places:
 *   - Settings -> Dashboard (a live mock of the real dashboard the user drags
 *     cards around in),
 *   - DashboardPage (renders each zone in the stored order, and skips hidden
 *     ids), and
 *   - dashboardPrefsService (reconciles whatever the server returns).
 *
 * The stored preference is
 *   {"version": 2,
 *    "hidden": [<ids>],
 *    "order":  {"main": [<ids>], "rail": [<ids>]},
 *    "spans":  {<id>: 4 | 6 | 8 | 12}}
 * on the server (accounts.UserNavigationPreference.dashboard_prefs). Everything
 * is stored as a DIFFERENCE from the defaults: only hidden ids, and only widths
 * the user actually changed. So a card added here later is visible, positioned
 * and sized by this registry for everyone who never touched it; a stale stored
 * id is ignored; a card missing from a stored order slides back into its
 * default zone at its default position — no migration in either direction. A
 * version-1 value (hidden only) keeps working: the missing order and spans just
 * resolve to the defaults.
 *
 * The hero/greeting card is deliberately NOT here: it is the page header, so it
 * is neither hideable, movable nor resizable.
 *
 * Hiding, moving and resizing are personal display preferences ON TOP of access
 * control — the feature-flag / permission gates in DashboardPage still run
 * first, so an id in this list never grants anything.
 */

import type { ComponentType, SVGProps } from 'react';
import {
  BellIcon,
  BoltIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  ClipboardIcon,
  IdentificationIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

export type DashboardCardId =
  | 'help-assistant'
  | 'registry-snapshot'
  | 'announcements'
  | 'endoscopy-schedule'
  | 'pending-tasks'
  | 'unread-messages'
  | 'smart-flow'
  | 'quick-access';

/**
 * Where a card sits on a desktop screen: the wide main column, or the narrow
 * right-hand rail. Below `lg` there is no rail — the rail cards stack under the
 * main column, in their rail order.
 */
export type DashboardZone = 'main' | 'rail';

/**
 * How much of the main column's width a card takes, in twelfths — the same
 * unit as the CSS grid it renders into. Cards flow left to right and wrap onto
 * a new row when the next one no longer fits, so 6 + 6 sit side by side and a
 * lone 6 takes half the row and leaves the rest empty.
 *
 * Only the main column is spannable: the rail is a fixed ~320px strip, so a
 * card in it is always the full width of that strip.
 */
export type DashboardSpan = 12 | 8 | 6 | 4;

export const DASHBOARD_SPANS: { value: DashboardSpan; label: string; short: string }[] = [
  { value: 12, label: 'Full width', short: 'Full' },
  { value: 8, label: 'Two thirds', short: '2/3' },
  { value: 6, label: 'Half', short: '1/2' },
  { value: 4, label: 'One third', short: '1/3' },
];

/**
 * What the dashboard does when the arranged cards are taller than the screen.
 *
 * 'fit'    - everything on one screen, no scrolling. Cards that cannot be
 *            shown at a usable size collapse to a one-line summary (name, a
 *            count of what is new, and a link to the app) instead of being
 *            clipped. Rows are given up from the BOTTOM, so the cards the user
 *            put at the top keep their full size.
 * 'scroll' - the column scrolls once there is more than fits. Identical to
 *            'fit' whenever everything already fits, so it is the default:
 *            its failure mode is a scrollbar, not a collapsed card.
 *
 * Both are desktop-only distinctions: below `lg` there is no rail and no height
 * cap, so the page scrolls either way.
 */
export type DashboardHeightMode = 'fit' | 'scroll';

export const DASHBOARD_HEIGHT_MODES: {
  value: DashboardHeightMode;
  label: string;
  hint: string;
}[] = [
  { value: 'scroll', label: 'Scroll', hint: 'Cards keep their size; scroll when there is more than fits.' },
  {
    value: 'fit',
    label: 'Fit to screen',
    hint: 'Never scrolls. Cards that do not fit shrink to a single line with a link and a count of anything new.',
  },
];

const HEIGHT_MODE_VALUES = new Set<string>(DASHBOARD_HEIGHT_MODES.map((m) => m.value));

export const DEFAULT_HEIGHT_MODE: DashboardHeightMode = 'scroll';

/** Accent colour of the card, mirrored by the Settings preview tile. */
export type DashboardCardAccent =
  | 'blue'
  | 'sky'
  | 'amber'
  | 'orange'
  | 'indigo'
  | 'emerald'
  | 'slate';

export interface DashboardCardDef {
  id: DashboardCardId;
  name: string;
  description: string;
  /** Feature flag that must be enabled for the card to exist at all. */
  featureCode?: string;
  /** Requires any admin level (mirrors the card's own gate). */
  adminOnly?: boolean;
  /** Zone the card starts in before the user rearranges anything. */
  zone: DashboardZone;
  /**
   * True only for cards that already have a narrow accordion render, so they
   * can be moved into (or out of) the right rail. Everything else is a
   * full-width card and stays in the main column.
   */
  railCapable?: boolean;
  /**
   * The app this card is a window onto. Used by the compact fallback in "Fit to
   * screen": a card that cannot be shown at full size collapses to one row that
   * links here. Omitted for cards that are not a view of any single app —
   * Help Assistant is a box on the dashboard itself, and Quick Access is a list
   * of links already.
   */
  appPath?: string;
  /** Width in the main column before the user changes it. */
  defaultSpan: DashboardSpan;
  /**
   * The card has content worth stretching (a list that can scroll), so a row
   * containing it takes the main column's leftover height instead of sitting
   * at its natural size.
   */
  growable?: boolean;
  /** Rough height of the tile in the Settings preview, so the mock reads true. */
  previewSize: 'sm' | 'md' | 'lg';
  accent: DashboardCardAccent;
  /** Same icon the real card wears, so the preview is recognisable. */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

/** Listed in real dashboard order — DEFAULT_DASHBOARD_LAYOUT is derived from it. */
export const DASHBOARD_CARDS: DashboardCardDef[] = [
  {
    id: 'help-assistant',
    name: 'Help Assistant',
    description: '"How can we help you?" — ask anything about using OneCare.',
    zone: 'main',
    defaultSpan: 12,
    previewSize: 'sm',
    accent: 'blue',
    icon: SparklesIcon,
  },
  {
    id: 'registry-snapshot',
    name: 'Patient Registry Snapshot',
    description: 'Census, admissions and discharges you can access, with a 7/30-day toggle.',
    featureCode: 'patient_registry',
    zone: 'main',
    appPath: '/onecare/patients',
    defaultSpan: 12,
    previewSize: 'md',
    accent: 'blue',
    icon: IdentificationIcon,
  },
  {
    id: 'endoscopy-schedule',
    name: 'Endoscopy Schedule',
    description: 'Occupied booking slots (doctor and procedure only) for your endoscopy unit.',
    featureCode: 'endoscopy_booking',
    zone: 'main',
    appPath: '/onecare/tools/endoscopy-booking',
    defaultSpan: 12,
    previewSize: 'md',
    accent: 'sky',
    icon: CalendarDaysIcon,
  },
  {
    id: 'announcements',
    name: 'Hospital Announcements',
    description: 'Latest hospital news and policy posts.',
    featureCode: 'announcements',
    zone: 'main',
    appPath: '/onecare/announcements',
    // Half width by default, so it shares a row with Pending Tasks exactly as
    // it always has -- now as a setting the user can change rather than a rule.
    defaultSpan: 6,
    growable: true,
    previewSize: 'lg',
    accent: 'amber',
    icon: BellIcon,
  },
  {
    id: 'unread-messages',
    name: 'Unread Messages',
    description: 'Senders with unread messages.',
    featureCode: 'messaging',
    zone: 'rail',
    railCapable: true,
    appPath: '/onecare/messages',
    defaultSpan: 12,
    previewSize: 'md',
    accent: 'emerald',
    icon: ChatBubbleLeftRightIcon,
  },
  {
    // Moved main -> rail on 2026-08-03, and listed straight after Unread
    // Messages so it lands directly under it in the rail. It is a short list
    // that reads fine at 320px, and the main column needed the height back.
    //
    // `railCapable` because it now has a narrow accordion render as well as
    // the wide card, so the user can still move it back to the main column.
    // Anyone who had already arranged their dashboard keeps their placement:
    // sanitizeDashboardLayout honours a stored zone, and only cards a stored
    // layout never mentioned fall back to this default.
    id: 'pending-tasks',
    name: 'Pending Tasks',
    description: 'Your active tasks and to-dos.',
    zone: 'rail',
    railCapable: true,
    appPath: '/onecare/tasks',
    defaultSpan: 12,
    growable: true,
    previewSize: 'md',
    accent: 'slate',
    icon: ClipboardIcon,
  },
  {
    id: 'smart-flow',
    name: 'Smart Flow Tasks',
    description: 'Your AI-powered task queue.',
    featureCode: 'smart_flow',
    zone: 'rail',
    railCapable: true,
    appPath: '/onecare/tools/smart-flow',
    defaultSpan: 12,
    previewSize: 'md',
    accent: 'indigo',
    icon: SparklesIcon,
  },
  {
    id: 'quick-access',
    name: 'Quick Access',
    description: 'Shortcut tiles to tools and applications.',
    zone: 'rail',
    railCapable: true,
    defaultSpan: 12,
    previewSize: 'md',
    accent: 'orange',
    icon: BoltIcon,
  },
];

export const DASHBOARD_CARDS_BY_ID: Record<DashboardCardId, DashboardCardDef> =
  DASHBOARD_CARDS.reduce((acc, card) => {
    acc[card.id] = card;
    return acc;
  }, {} as Record<DashboardCardId, DashboardCardDef>);

/** Which cards sit in which column, top to bottom, and how wide each one is. */
export interface DashboardLayout {
  main: DashboardCardId[];
  rail: DashboardCardId[];
  /**
   * Only widths the user actually changed. A card missing here uses its
   * `defaultSpan`, so a new card is sized by the registry rather than by a
   * stale stored value.
   */
  spans: Partial<Record<DashboardCardId, DashboardSpan>>;
  /** What happens when the arrangement is taller than the screen. */
  heightMode: DashboardHeightMode;
}

export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = {
  main: DASHBOARD_CARDS.filter((c) => c.zone === 'main').map((c) => c.id),
  rail: DASHBOARD_CARDS.filter((c) => c.zone === 'rail').map((c) => c.id),
  spans: {},
  heightMode: DEFAULT_HEIGHT_MODE,
};

const VALID_SPANS = new Set<number>(DASHBOARD_SPANS.map((s) => s.value));

/** The width this card is rendered at — the user's choice, else the default. */
export function spanOf(id: DashboardCardId, layout: DashboardLayout): DashboardSpan {
  return layout.spans[id] ?? DASHBOARD_CARDS_BY_ID[id].defaultSpan;
}

/**
 * Pack cards into rows of 12 twelfths, left to right, wrapping when the next
 * card no longer fits. Shared by DashboardPage and the Settings preview so the
 * two can never disagree about what sits next to what.
 */
export function packIntoRows(
  ids: DashboardCardId[],
  layout: DashboardLayout,
): DashboardCardId[][] {
  const rows: DashboardCardId[][] = [];
  let row: DashboardCardId[] = [];
  let used = 0;
  for (const id of ids) {
    const span = spanOf(id, layout);
    if (row.length > 0 && used + span > 12) {
      rows.push(row);
      row = [];
      used = 0;
    }
    row.push(id);
    used += span;
  }
  if (row.length > 0) rows.push(row);
  return rows;
}

const KNOWN_IDS = new Set<string>(DASHBOARD_CARDS.map((c) => c.id));

/** Keep only ids that exist in the live registry (stale ids are ignored). */
export function sanitizeHiddenCards(raw: unknown): DashboardCardId[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is DashboardCardId => typeof id === 'string' && KNOWN_IDS.has(id));
}

/**
 * Turn whatever is stored into a layout that names every card exactly once.
 *
 * Unknown ids are dropped, duplicates keep their first position, a card parked
 * in the rail that has no narrow render is pushed back to the main column, and
 * any card the stored value never mentioned is inserted at its default spot.
 * Junk in, sane layout out — this is the only place that guarantee is made.
 */
export function sanitizeDashboardLayout(raw: unknown): DashboardLayout {
  const source: Record<string, unknown> =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const placed = new Set<DashboardCardId>();

  const readZone = (zone: DashboardZone): DashboardCardId[] => {
    const stored = Array.isArray(source[zone]) ? (source[zone] as unknown[]) : [];
    const out: DashboardCardId[] = [];
    for (const value of stored) {
      if (typeof value !== 'string' || !KNOWN_IDS.has(value)) continue;
      const id = value as DashboardCardId;
      if (placed.has(id)) continue;
      // A full-width card cannot live in the 320px rail — it has no narrow
      // render, so let the "missing cards" pass below put it back in main.
      if (zone === 'rail' && !DASHBOARD_CARDS_BY_ID[id].railCapable) continue;
      placed.add(id);
      out.push(id);
    }
    return out;
  };

  const spans: Partial<Record<DashboardCardId, DashboardSpan>> = {};
  const storedSpans = source.spans;
  if (storedSpans && typeof storedSpans === 'object' && !Array.isArray(storedSpans)) {
    for (const [key, value] of Object.entries(storedSpans as Record<string, unknown>)) {
      if (!KNOWN_IDS.has(key)) continue;
      const span = typeof value === 'number' ? value : Number(value);
      // Only widths that differ from the default are worth keeping, so a
      // registry change moves everyone who never touched the setting.
      if (!VALID_SPANS.has(span)) continue;
      if (span === DASHBOARD_CARDS_BY_ID[key as DashboardCardId].defaultSpan) continue;
      spans[key as DashboardCardId] = span as DashboardSpan;
    }
  }

  const rawMode = source.heightMode;
  const heightMode: DashboardHeightMode =
    typeof rawMode === 'string' && HEIGHT_MODE_VALUES.has(rawMode)
      ? (rawMode as DashboardHeightMode)
      : DEFAULT_HEIGHT_MODE;

  const layout: DashboardLayout = {
    main: readZone('main'),
    rail: readZone('rail'),
    spans,
    heightMode,
  };

  for (const card of DASHBOARD_CARDS) {
    if (placed.has(card.id)) continue;
    const zone = layout[card.zone];
    const defaultIndex = DEFAULT_DASHBOARD_LAYOUT[card.zone].indexOf(card.id);
    zone.splice(defaultIndex < 0 ? zone.length : Math.min(defaultIndex, zone.length), 0, card.id);
  }

  return layout;
}

export function isDefaultDashboardLayout(layout: DashboardLayout): boolean {
  return (
    layout.main.join('|') === DEFAULT_DASHBOARD_LAYOUT.main.join('|') &&
    layout.rail.join('|') === DEFAULT_DASHBOARD_LAYOUT.rail.join('|') &&
    Object.keys(layout.spans).length === 0 &&
    layout.heightMode === DEFAULT_HEIGHT_MODE
  );
}

const HIDDEN_STORAGE_KEY = 'dashboard-hidden-cards';
const LAYOUT_STORAGE_KEY = 'dashboard-card-layout';

/**
 * Browser cache of the last saved preference, so a hard refresh straight onto
 * the dashboard paints the right cards in the right order instead of flashing
 * the default arrangement while the fetch is in flight. The server copy stays
 * authoritative.
 */
export function readCachedHiddenCards(): DashboardCardId[] {
  if (typeof window === 'undefined') return [];
  try {
    return sanitizeHiddenCards(JSON.parse(window.localStorage.getItem(HIDDEN_STORAGE_KEY) || '[]'));
  } catch {
    return [];
  }
}

export function cacheHiddenCards(hidden: DashboardCardId[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(HIDDEN_STORAGE_KEY, JSON.stringify(hidden));
  } catch {
    /* private mode / quota — server copy is authoritative */
  }
}

export function readCachedLayout(): DashboardLayout {
  if (typeof window === 'undefined') return sanitizeDashboardLayout(null);
  try {
    return sanitizeDashboardLayout(JSON.parse(window.localStorage.getItem(LAYOUT_STORAGE_KEY) || 'null'));
  } catch {
    return sanitizeDashboardLayout(null);
  }
}

export function cacheLayout(layout: DashboardLayout): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    /* private mode / quota — server copy is authoritative */
  }
}

export function clearCachedDashboardPrefs(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(HIDDEN_STORAGE_KEY);
    window.localStorage.removeItem(LAYOUT_STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
